import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useActivityStream, type StreamStatus } from '../lib/sse';
import { invalidateForActivityEvent } from '../lib/sse-invalidation';

/**
 * **الاتصال الحيّ الواحد للتطبيق كلّه** — بنصّ `frontend-design §1.3`: «الواجهة تفتح
 * اتصال SSE واحد… وجدول ثابت يحوله إلى invalidations».
 *
 * **العلّة التي أوجدت هذا الملف:** الاتصال كان يُفتح داخل `ActivityScreen` وحدها، فكان
 * «محرك الحيوية» يعمل **على شاشة واحدة من إحدى عشرة**. والنتيجة على المسار اليومي:
 * المالك على شاشة «اليوم» أو «المشروع» أو «الصندوق»، ثم يلتقط شيئًا من تيليجرام، أو
 * ينضج تذكير، أو تكتمل مهمة وكيل — **ولا يتحرك شيء أمامه**. لا رسالة ولا مؤشر:
 * الشاشة تبدو محدَّثة وهي ليست كذلك، وهذا أسوأ من خطأٍ ظاهر.
 *
 * وكان `refetchOnWindowFocus` يستر جزءًا منه — تُحدَّث الشاشة عند العودة إلى اللسان —
 * فيخفي العطب في التطوير حيث تُبدَّل النوافذ كثيرًا، ويُبقيه على الجوال حيث يبقى
 * اللسان مفتوحًا والمالك ينتظر.
 *
 * **ولماذا مزوّد لا نداء ثانٍ:** `EventSource` ثانٍ يعني اتصالين ومعالجةً مزدوجة لكل
 * حدث. فالمزوّد يملك الاتصال الوحيد، ويفعل شيئًا واحدًا: **يُبطل الكاشات لكل حدث
 * وارد** — وهذا ما يجعل كل شاشة حيّة.
 *
 * ## وسجلّ المشتركين ذهب مع مشتركه الوحيد (N-366)
 *
 * كان المزوّد يوزّع الحدث نفسه على مشتركين بعد الإبطال، ولم يشترك فيه قط إلا
 * **شاشة النشاط** (تُدرج الحدث في تغذيتها التراكمية). فلمّا حُذفت الشاشة بأمر المالك
 * بقي السجلّ — بدالّة `useActivityEvents` بلا نداء، واختباراتٍ تقيس توزيعًا لا
 * يصل أحدًا. وذلك **قدرةٌ بلا غرض**، ومعيار المالك المُعلن «كل شيء فيه ابغاه يكون
 * لهدف وغرض محدد». فإن عادت شاشةٌ تحتاج الأحداث نفسها فمكانها هنا، ويُكتب السجلّ
 * حينها بمشتركه الأول في يدٍ واحدة.
 *
 * وكلفته على الخادم مقيسة لا مقدَّرة: البثّ مدفوعٌ بناقل الأحداث (`activityBus`) لا
 * باستطلاع دوري — فاتصالٌ ساكن يكتب `: ping` كل ٢٥ ثانية ولا يمسّ القاعدة حتى يقع
 * حدث. أي أن إبقاءه مفتوحًا طوال الجلسة يكلّف نبضةً لا استعلامًا.
 */

const StreamContext = createContext<StreamStatus | null>(null);

export function ActivityStreamProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const status = useActivityStream((event) => {
    invalidateForActivityEvent(qc, event);
  });

  const value = useMemo<StreamStatus>(() => status, [status]);

  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
}

/**
 * حالة الاتصال للعرض. خارج المزوّد تُعاد `'connecting'`: شارة الحالة زينة على شاشة،
 * وإسقاط الشاشة كلها لأن شارتها بلا سياق مقايضة خاسرة.
 */
export function useActivityStreamStatus(): StreamStatus {
  return useContext(StreamContext) ?? 'connecting';
}
